'use client'

import { useEffect, useState } from 'react'
import { GoogleAnalytics } from '@next/third-parties/google'
import { getVisitorRegion } from '@/lib/analytics/region'

const ANALYTICS_CONSENT_KEY = 'analytics-consent'

export const setAnalyticsConsent = (consent: boolean) => {
  localStorage.setItem(ANALYTICS_CONSENT_KEY, consent.toString())
  // Dispatch an event so other components can react to the consent change
  window.dispatchEvent(new CustomEvent('analytics-consent-change', { detail: { consent } }))
}

export const getAnalyticsConsent = (): boolean => {
  return localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'true'
}

/** true = accepted, false = declined, null = never answered the banner */
export const getStoredAnalyticsConsent = (): boolean | null => {
  const value = localStorage.getItem(ANALYTICS_CONSENT_KEY)
  return value === null ? null : value === 'true'
}

interface AnalyticsConsentProps {
  gaId?: string
}

export const AnalyticsConsent = ({ gaId }: AnalyticsConsentProps) => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null)

  useEffect(() => {
    // Stored choice wins; with no choice, visitors outside Europe are tracked (GA has no
    // cookieless mode, so Europe waits for "Accept"). Signed-in members are treated exactly
    // like anonymous visitors (2026-10-17): being logged in is not consent to analytics.
    let cancelled = false
    const stored = getStoredAnalyticsConsent()
    if (stored !== null) {
      setHasConsent(stored)
    } else {
      getVisitorRegion().then(region => {
        if (!cancelled && getStoredAnalyticsConsent() === null) setHasConsent(region === 'other')
      })
    }

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent<{ consent: boolean }>) => {
      setHasConsent(event.detail.consent)
    }

    window.addEventListener('analytics-consent-change', handleConsentChange as EventListener)

    return () => {
      cancelled = true
      window.removeEventListener('analytics-consent-change', handleConsentChange as EventListener)
    }
  }, [])

  if (!hasConsent || !gaId) return null

  return <GoogleAnalytics gaId={gaId} />
}
